import { parseFile } from '../services/parser.js';
import { parseEmailLogFile } from '../services/email-log-parser.js';
import { addLogs, addEmailLogs, addImport, getAllImports, getAllLogs, getAllEmailLogs, deleteImport, deleteLogsByImport, deleteEmailLogsByImport, updateImport, getAllThreats, addThreats, getAllIpEnrichments, addIpEnrichment } from '../services/storage.js';
import { batchLookupIPs, updateGeoFromWhois, updateGeoFromShodan } from '../services/geoip.js';
import { enrichIP } from '../services/threat-intel.js';
import { whoisLookup } from '../services/whois.js';
import { detectThreats } from '../services/threat-detector.js';
import { html, concurrencyPool } from '../services/util.js';
import { ConfirmDialog } from './confirm-dialog.js';
import './toast-container.js';

const template = document.createElement('template');
template.innerHTML = `
  <div class="section-header">
    <h1>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;margin-right:8px">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
      Import Data
    </h1>
  </div>

  <div class="import-tabs">
    <button class="import-tab active" data-tab="signin">Sign-In Logs</button>
    <button class="import-tab" data-tab="email">Email Logs</button>
  </div>

  <!-- Sign-In Logs Import -->
  <div class="import-tab-content" data-tab="signin">
    <div class="drop-zone" id="drop-zone-signin">
      <div class="drop-icon">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      </div>
      <div class="drop-text">Drop CSV or JSON log files here, or click to browse</div>
      <div class="drop-hint">Supports EntraID and Google Workspace sign-in logs</div>
    </div>
    <input type="file" id="file-input-signin" accept=".csv,.json,.tsv" multiple style="display:none">
  </div>

  <!-- Email Logs Import -->
  <div class="import-tab-content" data-tab="email" style="display:none">
    <div class="drop-zone" id="drop-zone-email">
      <div class="drop-icon">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      </div>
      <div class="drop-text">Drop Exchange message tracking or Gmail audit log files here</div>
      <div class="drop-hint">Supports Exchange CSV and Gmail JSON audit logs</div>
    </div>
    <input type="file" id="file-input-email" accept=".csv,.json" multiple style="display:none">
  </div>

  <div id="import-progress" style="display:none; margin-top: 12px;">
    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
        <div id="progress-step" style="font-size:0.85rem;color:var(--text-muted)">Step: —</div>
        <div id="progress-elapsed" style="font-size:0.75rem;color:var(--text-muted);font-variant-numeric:tabular-nums">0s</div>
      </div>
      <div id="progress-text" style="margin-bottom: 8px;">Processing...</div>
      <div class="progress-track">
        <div id="progress-bar" class="progress-fill" style="width: 0%;"></div>
      </div>
      <div style="margin-top:8px;text-align:right">
        <button class="btn btn-sm btn-secondary" id="import-cancel-btn" style="font-size:0.75rem">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;margin-right:4px"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Cancel
        </button>
      </div>
    </div>
  </div>

  <div style="margin-top: 20px;">
    <div class="section-header">
      <h2>Import History</h2>
      <div class="section-actions">
        <button class="btn btn-sm btn-secondary" id="refresh-imports">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
          Refresh
        </button>
      </div>
    </div>
    <div id="import-list"></div>
  </div>
`;

export class ImportPane extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = template.innerHTML;
    this._elapsedTimer = null;
    this._abortController = null;
    this._processingFile = false;
    this._activeTab = 'signin';
  }

  connectedCallback() {
    // Tab switching
    this.querySelectorAll('.import-tab').forEach(tab => {
      tab.addEventListener('click', () => this.switchTab(tab.dataset.tab));
    });

    // Sign-in drop zone
    const dropZoneSignin = this.querySelector('#drop-zone-signin');
    const fileInputSignin = this.querySelector('#file-input-signin');
    dropZoneSignin.addEventListener('click', () => fileInputSignin.click());
    dropZoneSignin.addEventListener('dragover', (e) => { e.preventDefault(); dropZoneSignin.classList.add('drag-over'); });
    dropZoneSignin.addEventListener('dragleave', () => { dropZoneSignin.classList.remove('drag-over'); });
    dropZoneSignin.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZoneSignin.classList.remove('drag-over');
      this._activeTab = 'signin';
      this.handleFiles(e.dataTransfer.files);
    });
    fileInputSignin.addEventListener('change', () => {
      this._activeTab = 'signin';
      this.handleFiles(fileInputSignin.files);
      fileInputSignin.value = '';
    });

    // Email drop zone
    const dropZoneEmail = this.querySelector('#drop-zone-email');
    const fileInputEmail = this.querySelector('#file-input-email');
    dropZoneEmail.addEventListener('click', () => fileInputEmail.click());
    dropZoneEmail.addEventListener('dragover', (e) => { e.preventDefault(); dropZoneEmail.classList.add('drag-over'); });
    dropZoneEmail.addEventListener('dragleave', () => { dropZoneEmail.classList.remove('drag-over'); });
    dropZoneEmail.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZoneEmail.classList.remove('drag-over');
      this._activeTab = 'email';
      this.handleFiles(e.dataTransfer.files);
    });
    fileInputEmail.addEventListener('change', () => {
      this._activeTab = 'email';
      this.handleFiles(fileInputEmail.files);
      fileInputEmail.value = '';
    });

    this.querySelector('#import-cancel-btn').addEventListener('click', () => this.cancelImport());
    this.querySelector('#refresh-imports').addEventListener('click', () => this.refresh());

    this.refresh();
    document.addEventListener('threatscope:data-cleared', () => this.refresh());
  }

  switchTab(tab) {
    this._activeTab = tab;
    this.querySelectorAll('.import-tab').forEach(t => t.classList.toggle('active', t.dataset.tab === tab));
    this.querySelectorAll('.import-tab-content').forEach(c => {
      c.style.display = c.dataset.tab === tab ? '' : 'none';
    });
  }

  _setProgress(step, text, pct) {
    const stepEl = this.querySelector('#progress-step');
    const textEl = this.querySelector('#progress-text');
    const barEl = this.querySelector('#progress-bar');
    if (stepEl) stepEl.textContent = `Step: ${step}`;
    if (textEl) textEl.textContent = text;
    if (barEl && pct != null) barEl.style.width = `${Math.min(pct, 100)}%`;
  }

  _startElapsedTimer() {
    this._stopElapsedTimer();
    const start = Date.now();
    const elEl = this.querySelector('#progress-elapsed');
    if (!elEl) return;
    this._elapsedTimer = setInterval(() => {
      const secs = Math.floor((Date.now() - start) / 1000);
      elEl.textContent = secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
    }, 500);
  }

  _stopElapsedTimer() {
    if (this._elapsedTimer) {
      clearInterval(this._elapsedTimer);
      this._elapsedTimer = null;
    }
  }

  async handleFiles(files) {
    for (const file of files) {
      this._cancelled = false;
      const cancelBtn = this.querySelector('#import-cancel-btn');
      if (cancelBtn) cancelBtn.disabled = false;
      await this.processFile(file);
      if (this._cancelled) break;
    }
    this.refresh();
    document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
  }

  async processFile(file) {
    const progressEl = this.querySelector('#import-progress');
    progressEl.style.display = '';
    this._processingFile = true;
    this._abortController = null;

    this._setProgress('Parsing', `Parsing ${file.name}...`, 0);
    this._startElapsedTimer();

    try {
      if (this._activeTab === 'email') {
        await this._processEmailFile(file, progressEl);
      } else {
        await this._processSigninFile(file, progressEl);
      }
    } catch (e) {
      this._stopElapsedTimer();
      this._setProgress('Error', `Error processing ${file.name}: ${e.message}`, null);
      window.showToast(`Error processing ${file.name}: ${e.message}`, 'error');
      setTimeout(() => { progressEl.style.display = 'none'; this._processingFile = false; }, 5000);
    }
  }

  async _processSigninFile(file, progressEl) {
    const records = await parseFile(file);

    if (this._cancelled) { this._finishCancelled(file.name); return; }

    if (records.length === 0) {
      this._setProgress('Complete', `No records found in ${file.name}`, 100);
      this._stopElapsedTimer();
      setTimeout(() => { progressEl.style.display = 'none'; }, 3000);
      window.showToast(`No records found in ${file.name}`, 'warning');
      return;
    }

    const importRec = {
      filename: file.name,
      source: records[0]?.source || 'unknown',
      recordCount: records.length,
      status: 'processing',
      importedAt: new Date().toISOString(),
      threatCount: 0,
    };
    const importId = await addImport(importRec);

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    const logsWithImport = records.map(r => ({ ...r, importId }));
    this._setProgress('Saving', `Saving ${records.length} records...`, 5);
    await addLogs(logsWithImport);

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    const uniqueIPs = [...new Set(records.map(r => r.ip).filter(Boolean))];
    this._setProgress('GeoIP', `Resolving IP locations (${uniqueIPs.length} unique IPs)...`, 5);

    const enrichments = await batchLookupIPs(uniqueIPs, (done, total) => {
      const pct = 5 + (done / total) * 40;
      this._setProgress('GeoIP', `GeoIP: ${done}/${total}`, pct);
    });

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    this._setProgress('Threat Intel', `Checking threat intel feeds...`, 45);
    const publicEnrichments = enrichments.filter(e => e && !e.isPrivate);
    let intelDone = 0;
    await concurrencyPool(publicEnrichments, async (e) => {
      const threatIntel = await enrichIP(e.ip);
      const updated = { ...e, threatIntel };
      await addIpEnrichment(updated);
      e.threatIntel = threatIntel;
      intelDone++;
      const pct = 45 + (intelDone / enrichments.length) * 15;
      this._setProgress('Threat Intel', `Intel: ${intelDone}/${enrichments.length}`, pct);
    }, 20);

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    const failedGeoIPs = enrichments.filter(e => e && !e.isPrivate && (e.lookupFailed || (!e.lat && !e.fromWhois && !e.fromShodan)));
    if (failedGeoIPs.length > 0) {
      let geoDone = 0;
      for (const e of failedGeoIPs) {
        if (this._cancelled) { this._finishCancelled(file.name, importId); return; }
        try {
          const shodanData = e.threatIntel?.shodan;
          if (shodanData && shodanData.countryName) {
            const enriched = await updateGeoFromShodan(e.ip, shodanData);
            if (enriched) Object.assign(e, enriched);
          } else {
            const whoisData = await whoisLookup(e.ip);
            if (whoisData) {
              const enriched = await updateGeoFromWhois(e.ip, whoisData);
              if (enriched) Object.assign(e, enriched);
            }
          }
        } catch (err) {
          console.warn('[import] geo backfill failed for', e.ip, err.message);
        }
        geoDone++;
        const pct = 60 + (geoDone / failedGeoIPs.length) * 10;
        this._setProgress('Backfill', `Backfilling geo: ${geoDone}/${failedGeoIPs.length}`, pct);
      }
    }

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    this._setProgress('Detection', `Running threat detection...`, 70);
    const existingThreats = await getAllThreats();
    const allLogs = await getAllLogs({ importId });
    const newThreats = await detectThreats(allLogs, enrichments, existingThreats);

    if (newThreats.length > 0) {
      await addThreats(newThreats);
    }

    await updateImport(importId, {
      status: 'completed',
      threatCount: newThreats.length,
    });

    this._setProgress('Complete', `${file.name}: ${records.length} records, ${newThreats.length} threats found`, 100);
    this._stopElapsedTimer();

    window.showToast(`Imported ${records.length} records from ${file.name} (${newThreats.length} threats)`, newThreats.length > 0 ? 'warning' : 'success');

    setTimeout(() => { progressEl.style.display = 'none'; this._processingFile = false; }, 2000);
  }

  async _processEmailFile(file, progressEl) {
    const records = await parseEmailLogFile(file);

    if (this._cancelled) { this._finishCancelled(file.name); return; }

    if (records.length === 0) {
      this._setProgress('Complete', `No records found in ${file.name}`, 100);
      this._stopElapsedTimer();
      setTimeout(() => { progressEl.style.display = 'none'; }, 3000);
      window.showToast(`No records found in ${file.name}`, 'warning');
      return;
    }

    const importRec = {
      filename: file.name,
      source: records[0]?.source || 'unknown',
      recordCount: records.length,
      status: 'processing',
      importedAt: new Date().toISOString(),
      threatCount: 0,
    };
    const importId = await addImport(importRec);

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    const logsWithImport = records.map(r => ({ ...r, importId }));
    this._setProgress('Saving', `Saving ${records.length} records...`, 5);
    await addEmailLogs(logsWithImport);

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    const uniqueIPs = [...new Set(records.map(r => r.senderIp).filter(Boolean))];
    this._setProgress('GeoIP', `Resolving IP locations (${uniqueIPs.length} unique IPs)...`, 5);

    const enrichments = await batchLookupIPs(uniqueIPs, (done, total) => {
      const pct = 5 + (done / total) * 45;
      this._setProgress('GeoIP', `GeoIP: ${done}/${total}`, pct);
    });

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    this._setProgress('Threat Intel', `Checking threat intel feeds...`, 50);
    const publicEnrichments = enrichments.filter(e => e && !e.isPrivate);
    let intelDone = 0;
    await concurrencyPool(publicEnrichments, async (e) => {
      const threatIntel = await enrichIP(e.ip);
      const updated = { ...e, threatIntel };
      await addIpEnrichment(updated);
      e.threatIntel = threatIntel;
      intelDone++;
      const pct = 50 + (intelDone / enrichments.length) * 25;
      this._setProgress('Threat Intel', `Intel: ${intelDone}/${enrichments.length}`, pct);
    }, 20);

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    const failedGeoIPs = enrichments.filter(e => e && !e.isPrivate && (e.lookupFailed || (!e.lat && !e.fromWhois && !e.fromShodan)));
    if (failedGeoIPs.length > 0) {
      let geoDone = 0;
      for (const e of failedGeoIPs) {
        if (this._cancelled) { this._finishCancelled(file.name, importId); return; }
        try {
          const shodanData = e.threatIntel?.shodan;
          if (shodanData && shodanData.countryName) {
            const enriched = await updateGeoFromShodan(e.ip, shodanData);
            if (enriched) Object.assign(e, enriched);
          } else {
            const whoisData = await whoisLookup(e.ip);
            if (whoisData) {
              const enriched = await updateGeoFromWhois(e.ip, whoisData);
              if (enriched) Object.assign(e, enriched);
            }
          }
        } catch (err) {
          console.warn('[email-import] geo backfill failed for', e.ip, err.message);
        }
        geoDone++;
        const pct = 75 + (geoDone / failedGeoIPs.length) * 20;
        this._setProgress('Backfill', `Backfilling geo: ${geoDone}/${failedGeoIPs.length}`, pct);
      }
    }

    if (this._cancelled) { this._finishCancelled(file.name, importId); return; }

    await updateImport(importId, {
      status: 'completed',
      recordCount: records.length,
    });

    this._setProgress('Complete', `${file.name}: ${records.length} records imported`, 100);
    this._stopElapsedTimer();

    window.showToast(`Imported ${records.length} email records from ${file.name}`, 'success');

    setTimeout(() => { progressEl.style.display = 'none'; this._processingFile = false; }, 2000);
  }

  get _cancelled() {
    return this.__cancelled || false;
  }

  set _cancelled(val) {
    this.__cancelled = val;
  }

  cancelImport() {
    this._cancelled = true;
    const btn = this.querySelector('#import-cancel-btn');
    if (btn) btn.disabled = true;
  }

  _finishCancelled(filename, importId) {
    this._stopElapsedTimer();
    this._cancelled = false;
    this._processingFile = false;
    if (importId) {
      updateImport(importId, { status: 'cancelled' }).catch(() => {});
    }
    const progressEl = this.querySelector('#import-progress');
    this._setProgress('Cancelled', `${filename}: cancelled`, null);
    window.showToast(`Import cancelled: ${filename}`, 'info');
    setTimeout(() => { if (progressEl) progressEl.style.display = 'none'; }, 2000);
  }

  async refresh() {
    const list = this.querySelector('#import-list');
    const imports = await getAllImports();

    if (imports.length === 0) {
      list.innerHTML = `<div class="empty-state" style="padding:40px 20px">
        <div class="empty-icon">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.4">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </div>
        <div class="empty-text">No imports yet</div>
        <div class="empty-hint">Upload sign-in log or email log files to get started</div>
      </div>`;
      return;
    }

    list.innerHTML = imports.map(imp => {
      const isEmail = imp.source === 'exchange' || imp.source === 'gmail-email' || imp.filename?.match(/\.json$/i);
      return `<div class="import-item">
        <div class="import-info">
          <div class="import-filename">${html(imp.filename)}</div>
          <div class="import-meta">
            <span class="badge badge-source-${imp.source}">${imp.source}</span>
            ${imp.recordCount} records ${imp.threatCount != null ? `· ${imp.threatCount} threats` : ''} · ${new Date(imp.importedAt).toLocaleString()}
          </div>
        </div>
        <div class="import-status">
          <span class="badge badge-status-${imp.status === 'completed' ? 'resolved' : imp.status === 'cancelled' ? 'blocked' : 'investigating'}">${imp.status}</span>
        </div>
        <div class="import-actions">
          <button class="btn btn-sm btn-secondary" data-action="delete-import" data-id="${imp.id}" title="Delete import">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
          </button>
        </div>
      </div>`;
    }).join('');

    list.querySelectorAll('[data-action="delete-import"]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = Number(btn.dataset.id);
        const confirmed = await ConfirmDialog.show({
          title: 'Delete Import',
          message: 'This will permanently delete all logs and associated data from this import.',
          confirmText: 'Delete',
          variant: 'danger',
        });
        if (!confirmed) return;
        // Delete from both tables to be safe
        await deleteLogsByImport(id).catch(() => {});
        await deleteEmailLogsByImport(id).catch(() => {});
        await deleteImport(id);
        this.refresh();
        document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
        window.showToast('Import deleted', 'info');
      });
    });
  }

}

customElements.define('import-pane', ImportPane);
